import { priceHistory } from '../pricing-summary';
import { Component, Inject, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { RmsModule } from '@rms';
import { WebService, PopupService, lastValueFrom, RmsMessageService } from '@services';
import { formatCPriceDisplay, formatCurrencyDisplay } from '@interfaces';
import { SkuPriceHistoryComponent } from '../sku-price-history/sku-price-history.component';
import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';

@Component({
  selector: 'app-sku-price-update',
  imports: [RmsModule],
  templateUrl: './sku-price-update.component.html',
  styleUrls: ['./sku-price-update.component.scss']
})
export class SkuPriceUpdateComponent implements OnInit {
  SKUsToUpdate: any[] = [];
  saving = false;
  @Input() embedded = false;
  @Input() set skus(value: any[]) { this.SKUsToUpdate = (value || []).map(s => { const existing=this.SKUsToUpdate.find(item=>item.InventoryTypeID===s.InventoryTypeID); return existing ? {...s, PricingSelected:true, NotesEdited:existing.NotesEdited, pricing:existing.pricing} : {...s, PricingSelected:true, pricing:this.initialPricing(s)}; }); }
  @Output() removed = new EventEmitter<number[]>();
  @Output() dismissed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<any>();
  private finish(result?: any) { if (this.embedded) this.dismissed.emit(); else this.rmsPulldownRef.close(result); }
  CopyFieldToAll(sku:any, field:string) {
    if(this.saving || !['ReplacementFixed','ReplacementDiff','AskPriceFixed','AskPriceDiff','CurrentMarketPrice','Comments'].includes(field)) return;
    const value=sku.pricing[field];
    if(value == null || (field !== 'Comments' && (String(value).trim()==='' || !Number.isFinite(Number(value))))) return;
    for(const item of this.selectedSKUs) {
      const previous=item.LatestSavedPricing ?? priceHistory(item.LatestInventoryTypePrices)[0] ?? {};
      const pricing={...item.pricing};
      // A single-field copy must not turn untouched, blank fields into zero prices on save.
      for(const key of ['ReplacementFixed','ReplacementDiff','AskPriceFixed','AskPriceDiff','CurrentMarketPrice','Comments']) {
        if(pricing[key] == null) pricing[key]=previous[key] ?? (key==='Comments'?'':0);
      }
      item.pricing={...pricing,[field]:value,IsUpdated:true};
      if(field==='Comments') item.NotesEdited=true;
    }
  }

  constructor(
    private popup: PopupService,
    private rmsPulldownRef: RmsPulldownRef,
    @Inject(RMS_PULLDOWN_DATA) private pulldownData: any,
    private web: WebService,
    private message: RmsMessageService
  ) {
    this.SKUsToUpdate = (Array.isArray(pulldownData) ? pulldownData : []).map(sku => ({...sku, PricingSelected:true, pricing: {...sku.pricing}}));
  }

  private initialPricing(sku:any) {
    const previous=sku.LatestSavedPricing ?? priceHistory(sku.LatestInventoryTypePrices)[0] ?? {};
    const pricing={...sku.pricing};
    for(const key of ['ReplacementFixed','ReplacementDiff','AskPriceFixed','AskPriceDiff','CurrentMarketPrice']) pricing[key] ??= previous[key] ?? 0;
    pricing.Comments ??= previous.Comments ?? '';
    return {...pricing,IsUpdated:false};
  }
  ngOnInit() {
    this.SKUsToUpdate.forEach(sku => {
      sku.pricing = this.initialPricing(sku);
    });


  }

  updateNotes(sku:any, value:string) { sku.NotesEdited=true; sku.pricing.Comments=value; this.markSkuUpdated(sku); }

  markSkuUpdated(sku: any): void {
    sku.pricing.IsUpdated = true;
  }

  get selectedSKUs(): any[] { return this.SKUsToUpdate.filter(s => s.PricingSelected !== false); }
  get allSelected(): boolean { return this.SKUsToUpdate.length > 0 && this.selectedSKUs.length === this.SKUsToUpdate.length; }
  selectSku(sku:any, selected:boolean) {
    if(this.saving) return;
    sku.PricingSelected=selected;
    if(this.embedded && !selected) {
      this.SKUsToUpdate=this.SKUsToUpdate.filter(s=>s!==sku);
      this.removed.emit([sku.InventoryTypeID]);
    }
  }
  selectAll(selected: boolean) {
    if(this.saving) return;
    if(this.embedded && !selected) {
      const ids=this.SKUsToUpdate.map(s=>s.InventoryTypeID);
      this.SKUsToUpdate=[];
      this.removed.emit(ids);
    } else this.SKUsToUpdate.forEach(s=>s.PricingSelected=selected);
  }

  get canSave(): boolean {
    return this.selectedSKUs.length > 0 && this.selectedSKUs.every(s => this.canSaveSku(s));
  }

  canSaveSku(sku: any): boolean {
    return !this.saving && sku.PricingSelected !== false && !!sku.pricing?.IsUpdated &&
      ['AskPriceFixed', 'AskPriceDiff', 'ReplacementFixed', 'ReplacementDiff', 'CurrentMarketPrice']
        .every(key => Number.isFinite(Number(sku.pricing[key] ?? 0)));
  }

  get remainingSKUs(): number {
    return this.selectedSKUs.filter(s => !s.pricing?.IsUpdated).length;
  }

  ApplyPreviousPricing(sku?: any) {
    if (this.saving) return;
    for (const item of sku ? [sku] : this.selectedSKUs) {
      const previous = item.LatestSavedPricing ?? priceHistory(item.LatestInventoryTypePrices)[0];
      if (!previous || !Object.keys(previous).length) continue;
      item.pricing = { ...item.pricing,
        AskPriceFixed: previous.AskPriceFixed ?? 0,
        AskPriceDiff: previous.AskPriceDiff ?? 0,
        ReplacementFixed: previous.ReplacementFixed ?? 0,
        ReplacementDiff: previous.ReplacementDiff ?? 0,
        Comments: previous.Comments ?? '', IsUpdated: true };
    }
  }
  cancel() { if (!this.saving) this.finish(); }
  CopySKUToAll(sku?: any) {
    if (this.saving) return;
    if (!sku) {
      sku = this.selectedSKUs[0];
    }
    if (!sku) return;
    this.selectedSKUs.forEach(s => {
      s.pricing = {
        ...sku.pricing,
        IsUpdated: true,
        CurrentMarketPrice: s.pricing.CurrentMarketPrice
      };
    });
  }
  openSKUHistory(row: any) {
    const history = priceHistory(row.InventoryTypePricingHistory)
      .map((h: any) => ({
        ...h,
        AskPriceFixed: formatCurrencyDisplay(h.AskPriceFixed),
        AskPriceDiff: formatCPriceDisplay(h.AskPriceDiff),
        CurrentMarketPrice: formatCurrencyDisplay(h.CurrentMarketPrice),
        ReplacementFixed: formatCurrencyDisplay(h.ReplacementFixed),
        ReplacementDiff: formatCPriceDisplay(h.ReplacementDiff)
      }));

    this.popup.open({
      title: 'SKU Price History',
      data: { HistoricalPrices: history, CoffeeName: row.InventoryTypeName },
      component: SkuPriceHistoryComponent,
    });
  }
  async savePrices(sku?: any) {
    if (sku ? !this.SKUsToUpdate.includes(sku) || !this.canSaveSku(sku) : !this.canSave) return;
    const toSave = sku ? [sku] : this.selectedSKUs;

    const payload = {
      TraderGUID: localStorage.getItem('TraderGUID'),
      Prices: toSave.map(sku => ({
        InventoryTypeID: sku.InventoryTypeID,

        AskPriceFixed: Number(sku.pricing.AskPriceFixed ?? 0),
        AskPriceDiff: Number(sku.pricing.AskPriceDiff ?? 0),

        ReplacementFixed: Number(sku.pricing.ReplacementFixed ?? 0),
        ReplacementDiff: Number(sku.pricing.ReplacementDiff ?? 0),

        CurrentMarketPrice: Number(sku.pricing.CurrentMarketPrice ?? 0),

        Comments: sku.pricing.Comments || ''
      }))
    };

    this.saving = true;
    try {
      await lastValueFrom(this.web.getWebCall('inventory', 'coffee/price/save', {body:payload}));
      this.saved.emit({success:true, prices:payload.Prices});
      if (sku) {
        this.SKUsToUpdate = this.SKUsToUpdate.filter(item => item !== sku);
        if (!this.embedded) this.rmsPulldownRef.sendData({success:true});
        if (this.SKUsToUpdate.length) {
          this.message.success(`${sku.InventoryTypeName} pricing updated.`);
        } else {
          this.finish({success:true});
        }
      } else {
        this.SKUsToUpdate = this.SKUsToUpdate.filter(item => !toSave.includes(item));
        if (!this.embedded) this.rmsPulldownRef.sendData({success:true});
        if (!this.SKUsToUpdate.length) this.finish({success:true});
        else this.message.success(`${toSave.length} selected SKU(s) updated.`);
      }
    } catch { this.message.error('Could not save pricing. Your entries are still here; please retry.'); }
    finally { this.saving = false; }
  }
}
